import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { createLotusScene } from "../../lib/lotusScene";
import { createPond2D } from "../../lib/lotus2d";
import { immersionMode } from "../../lib/perf";
import { audio } from "../../lib/audio";
import { useIdle } from "./useIdle";
import { imageSrc } from "../../lib/api";

const POND_MS = 12000;
const NEXT = { calm: ["reflect", 3200], reflect: ["lift", 3400], lift: ["hold", 4800], hold: ["done", 6000] };

function Reveal2D({ painting, stage }) {
  const lifted = stage === "lift" || stage === "hold";
  const src = imageSrc(painting.image_url);
  const img = "block h-auto w-[min(70vw,calc(42vh*var(--ar)))]";
  return (
    <motion.div data-testid="lotus-reveal-2d" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.6 }} className="absolute inset-0 overflow-hidden" style={{ "--ar": painting.aspect_ratio || 1 }}>
      <motion.div animate={{ y: lifted ? "-24%" : "-50%" }} transition={{ duration: 4.4, ease: [0.45, 0, 0.25, 1] }} className="absolute inset-x-0 top-0 h-[200%]">
        <div className="relative flex h-1/2 items-end justify-center bg-[radial-gradient(ellipse_at_50%_55%,#F8EEDF_0%,#EADBC6_55%,#CDBBA6_100%)] pb-[6vh]">
          <motion.img data-testid="lotus-reveal-image" src={src} alt={painting.title} animate={{ opacity: lifted ? 1 : 0 }} transition={{ duration: 2 }} className={`${img} shadow-[0_24px_60px_-20px_rgba(60,40,30,0.55)] ring-4 ring-[#3B2E2A]`} />
        </div>
        <div className="relative flex h-1/2 justify-center bg-gradient-to-b from-[#566173] to-[#2F3445] pt-[6vh]">
          <img src={src} alt="" className={`${img} -scale-y-100 opacity-60 blur-[1px] [mask-image:linear-gradient(to_bottom,black,transparent_85%)]`} />
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function LotusExperience({ painting, onDone }) {
  const [mode, setMode] = useState(() => (immersionMode() === "full" ? "3d" : "2d"));
  const [stage, setStage] = useState("pond");
  const [touched, setTouched] = useState(false);
  const idle = useIdle(8000);
  const [ready, setReady] = useState(false);
  const canvasRef = useRef(null);
  const sceneRef = useRef(null);
  const stageRef = useRef(stage);
  stageRef.current = stage;
  const doneRef = useRef(onDone);
  doneRef.current = onDone;
  const [canGo, setCanGo] = useState(false);

  useEffect(() => {
    let s;
    try {
      s = mode === "3d" ? createLotusScene(canvasRef.current, painting, { onLowFps: () => setMode("2d") }) : createPond2D(canvasRef.current, painting);
    } catch {
      setMode("2d");
      return undefined;
    }
    s.setStage(stageRef.current);
    sceneRef.current = s;
    return () => s.destroy();
  }, [mode, painting]);

  useEffect(() => {
    sceneRef.current?.setStage(stage);
    const next = stage === "pond" ? ["calm", POND_MS] : NEXT[stage];
    if (!next) return undefined;
    const id = setTimeout(() => (next[0] === "done" ? doneRef.current() : setStage(next[0])), next[1]);
    const go = stage === "hold" ? setTimeout(() => setCanGo(true), 3000) : null;
    return () => {
      clearTimeout(id);
      clearTimeout(go);
    };
  }, [stage]);

  useEffect(() => {
    const a = setTimeout(() => setReady(true), 2500);
    const drip = setInterval(() => stageRef.current === "pond" && audio.plop?.(0.35), 3800);
    return () => {
      clearTimeout(a);
      clearInterval(drip);
    };
  }, []);

  const down = (e) => {
    setTouched(true);
    if (sceneRef.current?.down(e)) audio.plop?.(1);
  };
  const move = (e) => sceneRef.current?.move(e) && audio.plop?.(0.4);
  const up = () => sceneRef.current?.up();

  return (
    <div data-testid="lotus-experience" data-stage={stage} data-mode={mode} className="fixed inset-0 z-50 overflow-hidden bg-[#2F3445]">
      <canvas key={mode} ref={canvasRef} data-testid="lotus-canvas" className="absolute inset-0 h-full w-full touch-none" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} />
      {mode === "2d" && ["reflect", "lift", "hold"].includes(stage) && <Reveal2D painting={painting} stage={stage} />}
      <div className="grain pointer-events-none absolute inset-0 opacity-40" />
      <motion.p
        data-testid="lotus-emotion"
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: stage === "pond" ? 1 : 0, y: 0 }}
        transition={{ duration: 2, delay: stage === "pond" ? 1.2 : 0 }}
        className="pointer-events-none absolute inset-x-0 top-8 text-center font-display text-3xl italic text-white drop-shadow-[0_2px_14px_rgba(45,30,62,0.55)]"
      >
        {painting.feeling_word || painting.emotion}
      </motion.p>
      <AnimatePresence>
        {(!touched || idle) && stage === "pond" && (
          <motion.p key="hint" data-testid="play-hint" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.6, delay: 1.5 }} className="pointer-events-none absolute inset-x-0 bottom-36 text-center font-hand text-3xl text-white drop-shadow-[0_2px_10px_rgba(45,30,62,0.7)] sm:bottom-28">
            touch the water
          </motion.p>
        )}
        {stage === "calm" && (
          <motion.p key="calm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.4 }} className="pointer-events-none absolute inset-x-0 bottom-36 text-center font-display text-2xl italic text-white/90 drop-shadow-[0_2px_10px_rgba(45,30,62,0.6)] sm:bottom-28">
            and the water grows still
          </motion.p>
        )}
      </AnimatePresence>
      {canGo && stage === "hold" && (
        <motion.button
          data-testid="lotus-reveal-continue-button"
          onClick={() => doneRef.current()}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          style={{ x: "-50%" }}
          className="absolute bottom-20 left-1/2 rounded-full border border-white/70 bg-white/75 px-8 py-3 font-hand text-xl text-[#2D1E3E] backdrop-blur-md transition-colors duration-300 hover:bg-white sm:bottom-8"
        >
          continue
        </motion.button>
      )}
      {stage === "pond" && (
        <motion.button
          data-testid="lotus-continue-button"
          onClick={() => setStage("calm")}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: ready ? 1 : 0, y: ready ? 0 : 10 }}
          transition={{ duration: 1 }}
          style={{ x: "-50%" }}
          className="absolute bottom-20 left-1/2 rounded-full border border-white/70 bg-white/75 px-8 py-3 text-[11px] uppercase tracking-[0.3em] text-[#2D1E3E] shadow-[0_12px_30px_-10px_rgba(91,58,133,0.35)] backdrop-blur-md transition-colors duration-300 hover:bg-white sm:bottom-8"
        >
          Continue
        </motion.button>
      )}
    </div>
  );
}
